"use client";

import { motion } from "framer-motion";
import { ArrowRight, Bot, Code as Code2, Cpu, Sparkles, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const PROGRAMS = [{
  id: "robotics",
  label: "Robotics",
  viLabel: "Robotics",
  icon: Bot,
  colorClass: "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_robotics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_robotics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_robotics.png-wvc-sizes",
  alt: "Robotics kit and sensor assembly"
}, {
  id: "lego-robotics",
  label: "LEGO Robotics",
  viLabel: "LEGO Robotics",
  icon: Cpu,
  colorClass: "bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_lego_robotics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_lego_robotics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_lego_robotics.png-wvc-sizes",
  alt: "LEGO robotics building block set"
}, {
  id: "coding",
  label: "Coding",
  viLabel: "Lập trình",
  icon: Code2,
  colorClass: "bg-accent/10 text-accent-foreground group-hover:bg-accent group-hover:text-accent-foreground",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_coding.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_coding.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_coding.webp-wvc-sizes",
  alt: "Algorithmic coding interface"
}, {
  id: "engineering",
  label: "Engineering",
  viLabel: "Kỹ thuật",
  icon: Wrench,
  colorClass: "bg-chart-4/10 text-chart-4 group-hover:bg-chart-4 group-hover:text-white",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_engineering.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_engineering.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_engineering.png-wvc-sizes",
  alt: "Mechanical engineering project prototype"
}, {
  id: "stem-steam",
  label: "STEM / STEAM",
  viLabel: "STEM / STEAM",
  icon: Sparkles,
  colorClass: "bg-chart-5/10 text-foreground group-hover:bg-chart-5 group-hover:text-foreground",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_stem_steam.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_stem_steam.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/program_stem_steam.webp-wvc-sizes",
  alt: "STEM interactive lab workbench"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.215, 0.61, 0.355, 1]
    }
  }
};
export default function BeyondRobotics() {
  return <section id="beyond-robotics" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 text-foreground">
      {/* Decorative background shapes */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 size-96 rounded-full bg-secondary/10 blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }}>
            <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
              ROBOTO ECOSYSTEM
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl font-sans">
            KHÔNG CHỈ CÓ ROBOTICS
            <span className="block text-xl font-medium text-muted-foreground sm:text-2xl mt-1">
              Beyond Robotics
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base text-muted-foreground sm:text-lg leading-relaxed">
            Hệ sinh thái chương trình đào tạo đa dạng tại ROBOTO giúp học sinh phát triển toàn diện tư duy logic, sáng tạo và kỹ năng công nghệ cho tương lai.
          </motion.p>
        </div>

        {/* 5 Program Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 md:gap-6">
          {PROGRAMS.map((program, index) => {
          const Icon = program.icon;
          return <motion.div key={program.id} data-index={index} variants={cardVariants} whileHover={{
            y: -6
          }} transition={{
            duration: 0.2
          }}>
                <Card className="group h-full overflow-hidden border border-border/80 bg-card text-card-foreground shadow-xs transition-all duration-300 hover:border-primary/30 hover:shadow-lg">
                  <div className="relative h-28 w-full overflow-hidden bg-slate-950/10">
                    <img src={program.image} data-wvc-srcset={program.srcset} data-wvc-sizes={program.sizes} alt={program.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/25 to-transparent" />
                  </div>

                  <CardContent className="flex flex-col items-center justify-center p-5 pt-2 text-center">
                    <div className={`mb-3 flex size-12 items-center justify-center rounded-xl transition-colors duration-300 ${program.colorClass}`}>
                      <Icon className="size-6 stroke-[1.75]" />
                    </div>

                    <h3 className="text-lg font-semibold text-foreground tracking-tight">
                      {program.label}
                    </h3>
                    
                    {program.viLabel !== program.label && <span className="mt-1 text-xs text-muted-foreground font-medium">
                        {program.viLabel}
                      </span>}
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action / CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="mt-12 flex justify-center">
          <Button asChild size="lg" className="group rounded-full px-8 py-6 text-base font-semibold shadow-md transition-all hover:shadow-xl">
            <Link to="/ProgramsPage" className="inline-flex items-center gap-2">
              <span>XEM TẤT CẢ CHƯƠNG TRÌNH</span>
              <span className="opacity-80 text-xs font-normal border-l border-primary-foreground/30 pl-2">
                View All Programs
              </span>
              <ArrowRight className="size-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}